import { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, useApp, Typography, Alert, Space, Tag, Tooltip, theme } from '../ui';
import { Hash, ArrowLeft, MessageCircle, Zap, Copy } from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useLoginModal } from '../store/loginModal';
import { request } from '../api/client';
import { brand } from '../assets/brand';
import { PhoneInput } from './PhoneInput';
import { displayMobile } from '../lib/validators';

/**
 * WhatsApp-OTP sign-in rendered as a MODAL (replaces the old /login page). It is
 * mounted once at the app root and driven by the global `useLoginModal` store, so
 * the public-header "Login" button, the legacy /login URL, and the auth guards
 * all open the same dialog over whatever page the visitor is on.
 *
 * The two helper components below redirect the old /login route and unauthenticated
 * guard hits back to the home page while popping this modal.
 */
export function LoginModal() {
  const open = useLoginModal((s) => s.open);
  const redirectTo = useLoginModal((s) => s.redirectTo);
  const closeLogin = useLoginModal((s) => s.closeLogin);

  const { message } = useApp();
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const { token } = theme.useToken();
  const isDark = useThemeStore((s) => s.resolved)() === 'dark';
  const accent = isDark ? brand.primaryLight : brand.primaryDark;

  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [mobile, setMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [form] = Form.useForm<{ code: string }>();

  // Reset to a clean state every time the modal is (re)opened.
  useEffect(() => {
    if (open) { setStep('mobile'); setMobile(''); setSending(false); setDevCode(null); form.resetFields(); }
  }, [open, form]);

  const onRequestOtp = async () => {
    // `mobile` is already E.164 (from the country-aware PhoneInput); the server canonicalises it too.
    if (!mobile || !isValidPhoneNumber(mobile)) {
      message.warning('Enter a valid phone number.');
      return;
    }
    setSending(true);
    try {
      const r = await request<any>({ url: '/auth/login/otp/request', method: 'POST', data: { mobile } });
      if (r.delivery === 'failed') {
        message.warning('OTP generated but WhatsApp delivery failed — check Meta config.');
      } else {
        message.success(r.message ?? 'OTP sent via WhatsApp');
      }
      setDevCode(typeof r.devCode === 'string' ? r.devCode : null);
      setStep('otp');
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const onVerifyOtp = async (values: { code: string }) => {
    try {
      const r = await request<any>({ url: '/auth/login/otp/verify', method: 'POST', data: { mobile, code: values.code } });
      setTokens(r.accessToken, r.refreshToken, {
        id: r.user.id,
        fullName: r.user.fullName,
        role: r.user.role,
        roles: r.user.roles ?? [r.user.role],
        permissions: r.user.permissions,
        territoryId: r.user.territoryId,
      });
      message.success(`Welcome, ${r.user.fullName}`);
      closeLogin();
      // Back to where they were headed, else the per-role landing (RoleHome) —
      // never a hardcoded /dashboard (403s for Retailer/Farmer/Accounts).
      navigate(redirectTo || '/home');
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Invalid OTP');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) closeLogin(); }}
      width={420}
      title={
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 4 }}>
          <img src="/logo.png" alt="Green Bay Biotech International" style={{ height: 60, objectFit: 'contain' }} />
        </div>
      }
    >
      <div style={{ paddingTop: 8 }}>
        <Typography.Title level={4} style={{ color: accent, marginBottom: 4 }}>
          {step === 'mobile' ? 'Sign in' : 'Verify OTP'}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
          {step === 'mobile'
            ? 'We will send a one-time code to your WhatsApp.'
            : `Code sent via WhatsApp to ${displayMobile(mobile)}.`}
        </Typography.Paragraph>

        {step === 'mobile' && (
          <Form layout="vertical">
            <Form.Item label="Mobile number" required>
              <PhoneInput value={mobile} onChange={(v) => setMobile(v ?? '')} placeholder="Mobile number" />
            </Form.Item>
            <Button
              variant="primary"
              size="lg"
              block
              onClick={onRequestOtp}
              loading={sending}
              icon={<MessageCircle size={16} />}
              style={{ background: brand.primary, height: 48, fontWeight: 600 }}
            >
              Send OTP via WhatsApp
            </Button>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16, ...(isDark ? {} : { background: '#F0F7F0', border: '1px solid #E0EEDF' }) }}
              message={<span style={{ fontSize: 12 }}>New farmer? Your mobile becomes your account on first sign-in.</span>}
            />
          </Form>
        )}

        {step === 'otp' && (
          <Form layout="vertical" form={form} onFinish={onVerifyOtp}>
            {devCode && (
              <Alert
                type="warning"
                showIcon
                icon={<Zap size={16} />}
                style={{ marginBottom: 12, ...(isDark ? {} : { background: '#FFFBE6', border: '1px solid #FFE58F' }) }}
                message={
                  <Space>
                    <span style={{ fontSize: 12 }}>Dev mode — OTP:</span>
                    <Tag tone="gold" style={{ fontSize: 16, fontWeight: 700, letterSpacing: 2, padding: '2px 10px', margin: 0 }}>
                      {devCode}
                    </Tag>
                    <Tooltip title="Auto-fill the box below">
                      <Button size="sm" variant="text" icon={<Copy size={16} />} onClick={() => form.setFieldsValue({ code: devCode })}>
                        Use it
                      </Button>
                    </Tooltip>
                  </Space>
                }
                description={
                  <span style={{ fontSize: 11, color: isDark ? token.colorTextSecondary : '#8c6e00' }}>
                    Server is running in NODE_ENV=development. This banner is hidden in production builds.
                  </span>
                }
              />
            )}
            <Form.Item name="code" label="6-digit OTP" rules={[{ required: true, len: 6 }]}>
              <Input
                prefix={<Hash size={16} style={{ color: brand.primary }} />}
                placeholder="● ● ● ● ● ●"
                size="large"
                maxLength={6}
                autoFocus
                style={{ letterSpacing: 8, fontWeight: 600, fontSize: 18 }}
              />
            </Form.Item>
            <Button variant="primary" size="lg" type="submit" block style={{ background: brand.primary, height: 48, fontWeight: 600 }}>
              Verify &amp; Sign in
            </Button>
            <Button variant="text" icon={<ArrowLeft size={16} />} block style={{ marginTop: 8 }}
              onClick={() => { setStep('mobile'); setDevCode(null); form.resetFields(); }}>
              Use a different number
            </Button>
          </Form>
        )}

        <div style={{ marginTop: 24, fontSize: 11, color: token.colorTextTertiary, textAlign: 'center' }}>
          By signing in you agree to our terms · {brand.tagline}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Stand-in for the retired /login route: opens the login modal and bounces to the
 * public home page (so old bookmarks / links still land somewhere sensible). Honours
 * a `?redirect=` query param for post-login navigation.
 */
export function LoginRedirect() {
  const openLogin = useLoginModal((s) => s.openLogin);
  const [params] = useSearchParams();
  useEffect(() => { openLogin(params.get('redirect')); }, [openLogin, params]);
  return <Navigate to="/" replace />;
}

/**
 * Used by the auth guards when an unauthenticated user hits a protected page:
 * remember where they were headed, pop the login modal, and send them to home.
 */
export function RedirectToLogin({ from }: { from?: string }) {
  const openLogin = useLoginModal((s) => s.openLogin);
  useEffect(() => { openLogin(from ?? null); }, [openLogin, from]);
  return <Navigate to="/" replace />;
}
